import React, { useEffect, useRef, useState } from "react";
import {
  MessageCircle,
  X,
  ArrowUp,
  ArrowRight,
  ChevronLeft,
  Clapperboard,
  Clock,
  Wallet,
  Users,
  HelpCircle,
  Sparkles,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Ida. The heart of Brand New Money Films.
// The welcome always comes first. The chat is always available.

const QUICK_ACTIONS = [
  { icon: Clapperboard, label: "Our Services", message: "What services do you offer?" },
  { icon: Clock, label: "Production Process", message: "How does the production process work?" },
  { icon: Wallet, label: "Budget & Timelines", message: "How do budgets and timelines work?" },
  { icon: Users, label: "Talk to Our Team", message: "I\u2019d like to talk to your team." },
  { icon: HelpCircle, label: "General Questions", message: "I have a few general questions." },
  { icon: Sparkles, label: "Something Else", message: null },
];

function getSessionId() {
  let id = sessionStorage.getItem("bnm.ida.session");
  if (!id) {
    id = `ida_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("bnm.ida.session", id);
  }
  return id;
}

function loadThread() {
  try {
    const raw = sessionStorage.getItem("bnm.ida.thread.v2");
    if (raw) return JSON.parse(raw);
  } catch {
    // storage unavailable (private mode); start with an empty thread
  }
  return [];
}

const CloseButton = ({ onClose }) => (
  <button
    type="button"
    onClick={onClose}
    className="w-9 h-9 rounded-full border border-bnm flex items-center justify-center hover:border-black transition-colors shrink-0"
    data-testid="ida-close"
    aria-label="Close"
  >
    <X className="w-4 h-4" />
  </button>
);

const IdaWelcome = ({ welcomeRef, hasThread, onClose, onContinue, onQuickAction }) => (
  <div
    ref={welcomeRef}
    className="flex-1 min-h-0 overflow-y-auto px-7 pt-7 pb-6"
    data-testid="ida-welcome"
  >
    <div className="flex items-start justify-between">
      <div
        className="font-serif-italic font-light text-[17px] text-muted-ink/60 -rotate-6 select-none mt-1"
        aria-hidden
      >
        hello.
      </div>
      <CloseButton onClose={onClose} />
    </div>

    <h2
      className="font-serif-italic font-light text-ink text-[34px] leading-[1.1] tracking-[-0.01em] mt-4"
      data-testid="ida-welcome-heading"
    >
      Hi, I{"\u2019"}m <span style={{ color: "var(--bnm-denim)" }}>Ida</span>.
    </h2>
    <p className="text-ink text-[15px] leading-[1.6] mt-3">
      I{"\u2019"}m here to make your journey effortless.
    </p>

    <div className="rounded-2xl bg-[#f6f6f4] px-5 py-5 mt-6">
      <p className="text-[13.5px] text-ink leading-[1.7]">
        Whether you{"\u2019"}re exploring an idea, planning your next campaign, or simply curious about how we work, I{"\u2019"}ll help you find exactly what you need.
      </p>
      <p className="text-[13.5px] text-ink font-semibold leading-[1.7] mt-3">
        Think of me as your guide at Brand New Money Films.
      </p>
    </div>

    {hasThread && (
      <button
        type="button"
        onClick={onContinue}
        className="group w-full flex items-center justify-between gap-2 px-5 py-4 rounded-2xl border border-[#0a0a0a] bg-white text-left mt-6 transition-all duration-300 hover:shadow-[0_6px_18px_rgba(10,10,10,0.07)]"
        data-testid="ida-continue"
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <MessageCircle className="w-4 h-4 text-ink shrink-0" aria-hidden />
          <span className="text-[13px] text-ink font-medium">
            Continue previous conversation
          </span>
        </span>
        <ArrowRight
          className="w-3.5 h-3.5 text-ink shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
          aria-hidden
        />
      </button>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6" data-testid="ida-quick-actions">
      {QUICK_ACTIONS.map((a) => (
        <button
          key={a.label}
          type="button"
          onClick={() => onQuickAction(a)}
          className="group flex items-center justify-between gap-1.5 px-3.5 py-3.5 rounded-2xl border border-bnm bg-white text-left transition-all duration-300 hover:border-[#cfcfcf] hover:shadow-[0_4px_14px_rgba(10,10,10,0.05)]"
          data-testid={`ida-quick-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <a.icon className="w-4 h-4 text-muted-ink shrink-0" aria-hidden />
            <span className="text-[12px] text-ink whitespace-nowrap">{a.label}</span>
          </span>
          <ArrowRight
            className="w-3 h-3 text-muted-ink/60 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
            aria-hidden
          />
        </button>
      ))}
    </div>

    <div className="rounded-2xl bg-[#f6f6f4] px-5 py-4 mt-6" data-testid="ida-brand-message">
      <p className="text-[13px] text-ink font-semibold leading-[1.6]">
        Ida is the heart of Brand New Money Films.
      </p>
      <p className="text-[12.5px] text-muted-ink leading-[1.6] mt-1">
        Here to listen. Here to help. Here to make your journey effortless.
      </p>
    </div>
  </div>
);

const IdaChat = ({ chatRef, messages, sending, onBack, onClose }) => (
  <>
    <div className="flex items-center justify-between px-5 py-4 border-b border-bnm shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-full border border-bnm flex items-center justify-center hover:border-black transition-colors shrink-0"
          data-testid="ida-back-home"
          aria-label="Back to welcome"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <div className="font-serif-italic font-light text-[19px] text-ink leading-none">
            Ida
          </div>
          <div className="text-[11.5px] text-muted-ink mt-1 truncate" data-testid="ida-tagline">
            The heart of Brand New Money Films
          </div>
        </div>
      </div>
      <CloseButton onClose={onClose} />
    </div>

    <div
      ref={chatRef}
      className="flex-1 min-h-[240px] max-h-[46vh] sm:max-h-[380px] overflow-y-auto px-6 py-5 flex flex-col gap-3"
      data-testid="ida-messages"
    >
      {messages.map((m, i) => (
        <div
          key={m.id || `m-${i}`}
          className={`max-w-[85%] text-[13.5px] leading-[1.65] px-4 py-2.5 rounded-2xl animate-[fadeUp_250ms_ease-out_both] ${
            m.role === "user"
              ? "self-end bg-[#0a0a0a] text-white rounded-br-md"
              : "self-start bg-[#f6f6f4] text-ink rounded-bl-md"
          }`}
          data-testid={m.role === "user" ? "ida-message-user" : "ida-message-ida"}
        >
          {m.content}
        </div>
      ))}
      {sending && (
        <div
          className="self-start bg-[#f6f6f4] text-muted-ink text-[13.5px] px-4 py-2.5 rounded-2xl rounded-bl-md"
          data-testid="ida-typing"
        >
          <span className="inline-flex gap-1 items-center">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-ink/50 animate-pulse" />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-ink/50 animate-pulse [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-ink/50 animate-pulse [animation-delay:300ms]" />
          </span>
        </div>
      )}
    </div>
  </>
);

const IdaInput = ({ inputRef, input, setInput, sending, onSend }) => (
  <div className="shrink-0 border-t border-bnm px-4 pt-3 pb-2 bg-white">
    <div className="flex items-center gap-2 rounded-full border border-bnm bg-white pl-5 pr-1.5 py-1.5 focus-within:border-[#cfcfcf] transition-colors">
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSend();
        }}
        placeholder={"Ask Ida anything\u2026"}
        className="flex-1 text-[14px] outline-none bg-transparent placeholder:text-muted-ink/50 py-2"
        data-testid="ida-input"
      />
      <button
        type="button"
        onClick={() => onSend()}
        disabled={!input.trim() || sending}
        className="denim-surface denim-stitch w-10 h-10 rounded-full text-white flex items-center justify-center disabled:cursor-not-allowed shrink-0"
        data-testid="ida-send"
        aria-label="Send message"
      >
        <ArrowUp className="w-4 h-4" />
      </button>
    </div>
    <p className="text-[11px] text-muted-ink/70 mt-1.5 pl-2">Ida replies in moments.</p>
  </div>
);

export default function IdaWidget() {
  const [open, setOpen] = useState(false);
  // "welcome" is Ida's home. Every open starts here.
  const [view, setView] = useState("welcome");
  const [messages, setMessages] = useState(loadThread);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const welcomeRef = useRef(null);
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  // Persist thread + keep chat pinned to the latest message.
  useEffect(() => {
    try {
      sessionStorage.setItem("bnm.ida.thread.v2", JSON.stringify(messages.slice(-30)));
    } catch {
      // storage unavailable (private mode); thread persistence is optional
    }
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, sending, view]);

  // Every open returns to the top of the welcome. Never mid-conversation.
  useEffect(() => {
    if (!open) return;
    setView("welcome");
    requestAnimationFrame(() => {
      if (welcomeRef.current) welcomeRef.current.scrollTop = 0;
    });
    setTimeout(() => inputRef.current?.focus(), 400);
  }, [open]);

  const goWelcome = () => {
    setView("welcome");
    requestAnimationFrame(() => {
      if (welcomeRef.current) welcomeRef.current.scrollTop = 0;
    });
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setView("chat");
    setMessages((m) => [...m, { id: mid(), role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/ida/chat`, {
        session_id: getSessionId(),
        message: text,
      });
      setMessages((m) => [...m, { id: mid(), role: "assistant", content: res.data.reply }]);
    } catch (err) {
      console.warn("Ida chat request failed:", err?.message);
      setMessages((m) => [
        ...m,
        {
          id: mid(),
          role: "assistant",
          content:
            "I\u2019m having a little trouble connecting right now. You can always reach the team at ida@brandnewmoneyfilms.com.",
        },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const onQuickAction = (action) => {
    if (action.message) {
      send(action.message);
    } else {
      inputRef.current?.focus();
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="denim-surface denim-stitch fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[55] flex items-center gap-2.5 rounded-full text-white pl-4 pr-5 py-3 text-[13px] tracking-[0.02em] transition-all duration-300 hover:-translate-y-0.5 animate-[fadeIn_400ms_ease-out_both]"
          data-testid="ida-launcher"
          aria-label="Ask Ida"
        >
          <MessageCircle className="w-4 h-4" />
          <span>Ask Ida</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[55] w-[calc(100vw-40px)] sm:w-[440px] max-h-[85vh] bg-white border border-bnm rounded-[24px] shadow-[0_20px_60px_rgba(10,10,10,0.10)] flex flex-col overflow-hidden animate-[idaOpen_350ms_ease-out_both]"
          data-testid="ida-panel"
        >
          {view === "welcome" ? (
            <IdaWelcome
              welcomeRef={welcomeRef}
              hasThread={messages.length > 0}
              onClose={() => setOpen(false)}
              onContinue={() => setView("chat")}
              onQuickAction={onQuickAction}
            />
          ) : (
            <IdaChat
              chatRef={chatRef}
              messages={messages}
              sending={sending}
              onBack={goWelcome}
              onClose={() => setOpen(false)}
            />
          )}

          {/* Sticky input. Always visible, in both views. */}
          <IdaInput
            inputRef={inputRef}
            input={input}
            setInput={setInput}
            sending={sending}
            onSend={send}
          />
        </div>
      )}
    </>
  );
}
